'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
function _interopDefault(ex) {
  return ex && typeof ex === 'object' && 'default' in ex ? ex['default'] : ex;
}
var React = _interopDefault(require('react'));
var functions = require('@keytrade/functions');
var styled = _interopDefault(require('styled-components'));
var commonjsGlobal = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};
function createCommonjsModule(fn, module) {
  return module = {
    exports: {}
  }, fn(module, module.exports), module.exports;
}
var focusVisible = createCommonjsModule(function (module, exports) {
  (function (global, factory) {
    factory();
  })(commonjsGlobal, function () {
    /**
     * Applies the :focus-visible polyfill at the given scope.
     * A scope in this case is either the top-level Document or a Shadow Root.
     *
     * @param {(Document|ShadowRoot)} scope
     * @see https://github.com/WICG/focus-visible
     */

    function applyFocusVisiblePolyfill(scope) {
      var hadKeyboardEvent = true;
      var hadFocusVisibleRecently = false;
      var hadFocusVisibleRecentlyTimeout = null;
      var inputTypesAllowlist = {
        text: true,
        search: true,
        url: true,
        tel: true,
        email: true,
        password: true,
        number: true,
        date: true,
        month: true,
        week: true,
        time: true,
        datetime: true,
        'datetime-local': true
      };
      /**
       * Helper function for legacy browsers and iframes which sometimes focus
       * elements like document, body, and non-interactive SVG.
       * @param {Element} el
       */

      function isValidFocusTarget(el) {
        if (el && el !== document && el.nodeName !== 'HTML' && el.nodeName !== 'BODY' && 'classList' in el && 'contains' in el.classList) {
          return true;
        }
        return false;
      }
      /**
       * Computes whether the given element should automatically trigger the
       * `focus-visible` class being added, i.e. whether it should always match
       * `:focus-visible` when focused.
       * @param {Element} el
       * @return {boolean}
       */

      function focusTriggersKeyboardModality(el) {
        var type = el.type;
        var tagName = el.tagName;
        if (tagName === 'INPUT' && inputTypesAllowlist[type] && !el.readOnly) {
          return true;
        }
        if (tagName === 'TEXTAREA' && !el.readOnly) {
          return true;
        }
        if (el.isContentEditable) {
          return true;
        }
        return false;
      }
      /**
       * Add the `focus-visible` class to the given element if it was not added by
       * the author.
       * @param {Element} el
       */

      function addFocusVisibleClass(el) {
        if (el.classList.contains('focus-visible')) {
          return;
        }
        el.classList.add('focus-visible');
        el.setAttribute('data-focus-visible-added', '');
      }
      /**
       * Remove the `focus-visible` class from the given element if it was not
       * originally added by the author.
       * @param {Element} el
       */

      function removeFocusVisibleClass(el) {
        if (!el.hasAttribute('data-focus-visible-added')) {
          return;
        }
        el.classList.remove('focus-visible');
        el.removeAttribute('data-focus-visible-added');
      }
      /**
       * If the most recent user interaction was via the keyboard;
       * and the key press did not include a meta, alt/option, or control key;
       * then the modality is keyboard. Otherwise, the modality is not keyboard.
       * Apply `focus-visible` to any current active element and keep track
       * of our keyboard modality state with `hadKeyboardEvent`.
       * @param {KeyboardEvent} e
       */

      function onKeyDown(e) {
        if (e.metaKey || e.altKey || e.ctrlKey) {
          return;
        }
        if (isValidFocusTarget(scope.activeElement)) {
          addFocusVisibleClass(scope.activeElement);
        }
        hadKeyboardEvent = true;
      }
      /**
       * If at any point a user clicks with a pointing device, ensure that we change
       * the modality away from keyboard.
       * This avoids the situation where a user presses a key on an already focused
       * element, and then clicks on a different element, focusing it with a
       * pointing device, while we still think we're in keyboard modality.
       * @param {Event} e
       */

      function onPointerDown(e) {
        hadKeyboardEvent = false;
      }
      /**
       * On `focus`, add the `focus-visible` class to the target if:
       * - the target received focus as a result of keyboard navigation, or
       * - the event target is an element that will likely require interaction
       *   via the keyboard (e.g. a text box)
       * @param {Event} e
       */

      function onFocus(e) {
        // Prevent IE from focusing the document or HTML element.
        if (!isValidFocusTarget(e.target)) {
          return;
        }
        if (hadKeyboardEvent || focusTriggersKeyboardModality(e.target)) {
          addFocusVisibleClass(e.target);
        }
      }
      /**
       * On `blur`, remove the `focus-visible` class from the target.
       * @param {Event} e
       */

      function onBlur(e) {
        if (!isValidFocusTarget(e.target)) {
          return;
        }
        if (e.target.classList.contains('focus-visible') || e.target.hasAttribute('data-focus-visible-added')) {
          // To detect a tab/window switch, we look for a blur event followed
          // rapidly by a visibility change.
          // If we don't see a visibility change within 100ms, it's probably a
          // regular focus change.
          hadFocusVisibleRecently = true;
          window.clearTimeout(hadFocusVisibleRecentlyTimeout);
          hadFocusVisibleRecentlyTimeout = window.setTimeout(function () {
            hadFocusVisibleRecently = false;
          }, 100);
          removeFocusVisibleClass(e.target);
        }
      }
      /**
       * If the user changes tabs, keep track of whether or not the previously
       * focused element had .focus-visible.
       * @param {Event} e
       */

      function onVisibilityChange(e) {
        if (document.visibilityState === 'hidden') {
          // If the tab becomes active again, the browser will handle calling focus
          // on the element (Safari actually calls it twice).
          // If this tab change caused a blur on an element with focus-visible,
          // re-apply the class when the user switches back to the tab.
          if (hadFocusVisibleRecently) {
            hadKeyboardEvent = true;
          }
          addInitialPointerMoveListeners();
        }
      }
      /**
       * Add a group of listeners to detect usage of any pointing devices.
       * These listeners will be added when the polyfill first loads, and anytime
       * the window is blurred, so that they are active when the window regains
       * focus.
       */

      function addInitialPointerMoveListeners() {
        document.addEventListener('mousemove', onInitialPointerMove);
        document.addEventListener('mousedown', onInitialPointerMove);
        document.addEventListener('mouseup', onInitialPointerMove);
        document.addEventListener('pointermove', onInitialPointerMove);
        document.addEventListener('pointerdown', onInitialPointerMove);
        document.addEventListener('pointerup', onInitialPointerMove);
        document.addEventListener('touchmove', onInitialPointerMove);
        document.addEventListener('touchstart', onInitialPointerMove);
        document.addEventListener('touchend', onInitialPointerMove);
      }
      function removeInitialPointerMoveListeners() {
        document.removeEventListener('mousemove', onInitialPointerMove);
        document.removeEventListener('mousedown', onInitialPointerMove);
        document.removeEventListener('mouseup', onInitialPointerMove);
        document.removeEventListener('pointermove', onInitialPointerMove);
        document.removeEventListener('pointerdown', onInitialPointerMove);
        document.removeEventListener('pointerup', onInitialPointerMove);
        document.removeEventListener('touchmove', onInitialPointerMove);
        document.removeEventListener('touchstart', onInitialPointerMove);
        document.removeEventListener('touchend', onInitialPointerMove);
      }
      /**
       * When the polfyill first loads, assume the user is in keyboard modality.
       * If any event is received from a pointing device (e.g. mouse, pointer,
       * touch), turn off keyboard modality.
       * This accounts for situations where focus enters the page from the URL bar.
       * @param {Event} e
       */

      function onInitialPointerMove(e) {
        // Work around a Safari quirk that fires a mousemove on <html> whenever the
        // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
        if (e.target.nodeName && e.target.nodeName.toLowerCase() === 'html') {
          return;
        }
        hadKeyboardEvent = false;
        removeInitialPointerMoveListeners();
      } // For some kinds of state, we are interested in changes at the global scope
      // only. For example, global pointer input, global key presses and global
      // visibility change should affect the state at every scope:

      document.addEventListener('keydown', onKeyDown, true);
      document.addEventListener('mousedown', onPointerDown, true);
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('touchstart', onPointerDown, true);
      document.addEventListener('visibilitychange', onVisibilityChange, true);
      addInitialPointerMoveListeners(); // For focus and blur, we specifically care about state changes in the local
      // scope. This is because focus / blur events that originate from within a
      // shadow root are not re-dispatched from the host element if it was already
      // the active element in its own scope:

      scope.addEventListener('focus', onFocus, true);
      scope.addEventListener('blur', onBlur, true); // We detect that a node is a ShadowRoot by ensuring that it is a
      // DocumentFragment and also has a host property. This check covers native
      // implementation and polyfill implementation transparently. If we only cared
      // about the native implementation, we could just check if the scope was
      // an instance of a ShadowRoot.

      if (scope.nodeType === Node.DOCUMENT_FRAGMENT_NODE && scope.host) {
        // Since a ShadowRoot is a special kind of DocumentFragment, it does not
        // have a root element to add a class to. So, we add this attribute to the
        // host element instead:
        scope.host.setAttribute('data-js-focus-visible', '');
      } else if (scope.nodeType === Node.DOCUMENT_NODE) {
        document.documentElement.classList.add('js-focus-visible');
        document.documentElement.setAttribute('data-js-focus-visible', '');
      }
    } // It is important to wrap all references to global window and document in
    // these checks to support server-side rendering use cases
    // @see https://github.com/WICG/focus-visible/issues/199

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Make the polyfill helper globally available. This can be used as a signal
      // to interested libraries that wish to coordinate with the polyfill for e.g.,
      // applying the polyfill to a shadow root:
      window.applyFocusVisiblePolyfill = applyFocusVisiblePolyfill; // Notify interested libraries of the polyfill's presence, in case the
      // polyfill was loaded lazily:

      var event;
      try {
        event = new CustomEvent('focus-visible-polyfill-ready');
      } catch (error) {
        // IE11 does not support using CustomEvent as a constructor directly:
        event = document.createEvent('CustomEvent');
        event.initCustomEvent('focus-visible-polyfill-ready', false, false, {});
      }
      window.dispatchEvent(event);
    }
    if (typeof document !== 'undefined') {
      // Apply the polyfill to the global document, so that no JavaScript
      // coordination is required to use the polyfill in the top-level document:
      applyFocusVisiblePolyfill(document);
    }
  });
});
var colors = functions.config.colors.list;
var DownloadIcon = function DownloadIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M16.437 11.306l-4.432 4.307a.368.368 0 0 1-.51 0l-4.432-4.307a.34.34 0 0 1 0-.493l.587-.569a.363.363 0 0 1 .512.006l2.806 2.818v-7.22c0-.191.162-.348.36-.348h.838c.198 0 .36.157.36.348v7.22l2.806-2.818a.369.369 0 0 1 .512-.006l.587.57a.336.336 0 0 1 .006.492zm.704 5.685H6.359A.355.355 0 0 0 6 17.34v.813c0 .191.162.348.36.348h10.78c.198 0 .36-.157.36-.348v-.813a.355.355 0 0 0-.36-.348z"
  }));
};
var PdfIcon = function PdfIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Symbols",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    id: "IcnPDFLight",
    fill: colors.GreyBlue
  }, /*#__PURE__*/React.createElement("path", {
    d: "M17.5960938,7.77484866 L15.1927083,5.39923843 C14.9348958,5.14440539 14.5854167,5 14.2216146,5 L8.375,5 C7.61588542,5.00283148 7,5.6115993 7,6.36194103 L7,18.1408905 C7,18.8912322 7.61588542,19.5 8.375,19.5 L16.625,19.5 C17.3841146,19.5 18,18.8912322 18,18.1408905 L18,8.73755126 C18,8.37795352 17.8539063,8.0296817 17.5960938,7.77484866 Z M16.9486979,8.41759422 C17.0088542,8.47705526 17.0489583,8.54784222 17.0690104,8.62712361 L14.3333333,8.62712361 L14.3333333,5.9230619 C14.4135417,5.94288225 14.4851562,5.98252294 14.5453125,6.04198399 L16.9486979,8.41759422 Z M16.625,18.593927 L8.375,18.593927 C8.12291667,18.593927 7.91666667,18.3900605 7.91666667,18.1408905 L7.91666667,6.36194103 C7.91666667,6.11277094 8.12291667,5.90890451 8.375,5.90890451 L13.4166667,5.90890451 L13.4166667,8.85364187 C13.4166667,9.23022847 13.7231771,9.53319664 14.1041667,9.53319664 L17.0833333,9.53319664 L17.0833333,18.1408905 C17.0833333,18.3900605 16.8770833,18.593927 16.625,18.593927 Z M15.9947917,13.7464362 C15.6080729,13.3698496 14.4192708,13.4859402 13.8835937,13.5567272 C13.2763021,13.194298 12.8752604,12.6959578 12.5916667,11.9541105 C12.7148438,11.4444444 12.9354167,10.6176528 12.775,10.1164812 C12.6489583,9.32083577 11.6377604,9.41710603 11.4973958,9.92394064 C11.3541667,10.4421012 11.4888021,11.181117 11.7380208,12.1268307 C11.3971354,12.9309705 10.8872396,14.0210896 10.5320312,14.6355204 C9.93619792,14.9384886 8.98229167,15.4651435 8.84765625,16.119215 C8.74739583,16.5949033 9.50364583,17.2348174 10.36875,16.3004296 C10.6294271,16.0201133 10.9216146,15.598223 11.2653646,15.012107 C12.0302083,14.7629369 12.8723958,14.4514743 13.6143229,14.3325522 C14.2416667,14.6723296 14.9778646,14.8960164 15.4648438,14.8960164 C16.2583333,14.8988479 16.2927083,14.04091 15.9947917,13.7464362 L15.9947917,13.7464362 Z M9.42916667,16.2664519 C9.59817708,15.8162468 10.2484375,15.2924234 10.4460937,15.1112087 C9.81302083,16.1107206 9.42916667,16.2862722 9.42916667,16.2664519 Z M12.15625,9.97207577 C12.4054687,9.97207577 12.3796875,11.0338801 12.2164062,11.3198594 C12.0674479,10.8583285 12.0731771,9.97207577 12.15625,9.97207577 Z M11.3427083,14.4826206 C11.6664063,13.9219879 11.9442708,13.259422 12.1677083,12.678969 C12.4455729,13.1801406 12.8007812,13.5765475 13.1731771,13.8540324 C12.4770833,13.9871119 11.8726562,14.2815856 11.3427083,14.4826206 Z M15.7369792,14.3155634 C15.7369792,14.3155634 15.5708333,14.5137668 14.4908854,14.0578988 C15.6653646,13.9729545 15.8572917,14.2391135 15.7369792,14.3155634 Z",
    id: "Mask"
  }))));
};
var ExcelIcon = function ExcelIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    fill: colors.GreyBlue,
    fillRule: "evenodd",
    d: "M17.096 7.295l-2.403-2.371a1.384 1.384 0 0 0-.971-.399H7.875A1.37 1.37 0 0 0 6.5 5.885v11.758c0 .75.616 1.357 1.375 1.357h8.25c.76 0 1.375-.608 1.375-1.357V8.256c0-.359-.146-.706-.404-.96zm-.647.642c.06.06.1.13.12.21h-2.736v-2.7c.08.02.152.06.212.119l2.404 2.371zm-.324 10.159h-8.25a.457.457 0 0 1-.458-.453V5.885c0-.249.206-.452.458-.452h5.042v2.94c0 .375.306.678.687.678h2.98v8.592a.457.457 0 0 1-.459.453zm-3.56-4.862l1.446-2.312a.34.34 0 0 0-.295-.515h-.118a.346.346 0 0 0-.289.156c-.888 1.37-1.043 1.512-1.309 2.106-.493-.91-.24-.453-1.312-2.106a.346.346 0 0 0-.29-.156h-.117a.338.338 0 0 0-.292.515l1.467 2.312-1.693 2.53a.34.34 0 0 0 .29.523h.1c.117 0 .226-.06.289-.156 1.065-1.64 1.297-1.766 1.558-2.332.902 1.603 1.269 1.9 1.558 2.335a.342.342 0 0 0 .287.153h.103a.337.337 0 0 0 .29-.52l-1.674-2.533z"
  }));
};
var CsvIcon = function CsvIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    fill: colors.GreyBlue,
    fillRule: "evenodd",
    d: "M9.922 12.706a.463.463 0 0 0-.397.192c-.093.129-.14.308-.14.537 0 .478.179.716.537.716a1.7 1.7 0 0 0 .547-.112v.38c-.176.074-.372.11-.59.11-.31 0-.549-.094-.714-.283-.165-.19-.248-.46-.248-.814 0-.223.041-.418.122-.585a.879.879 0 0 1 .35-.385c.151-.09.33-.134.533-.134.208 0 .417.05.627.15l-.146.37a2.425 2.425 0 0 0-.242-.1.721.721 0 0 0-.239-.042zm2.266 1.2c0 .193-.07.345-.208.456-.14.112-.333.167-.581.167-.229 0-.43-.043-.607-.129v-.421a2.5 2.5 0 0 0 .367.136c.1.026.192.04.275.04a.39.39 0 0 0 .23-.058c.053-.038.08-.095.08-.17a.188.188 0 0 0-.036-.112.393.393 0 0 0-.103-.094 2.656 2.656 0 0 0-.278-.145 1.316 1.316 0 0 1-.294-.178.665.665 0 0 1-.157-.197.564.564 0 0 1-.059-.264c0-.19.065-.338.193-.447.128-.108.306-.162.532-.162.112 0 .218.013.32.04.1.026.206.063.316.11l-.146.354a2.143 2.143 0 0 0-.284-.099.912.912 0 0 0-.22-.027c-.086 0-.152.02-.198.06a.198.198 0 0 0-.069.156c0 .04.01.075.028.105.019.03.048.059.089.087.04.027.136.077.288.15.2.095.337.191.411.288a.562.562 0 0 1 .111.353zm1.095.198a.73.73 0 0 1 0-.003v.003zm0-.003a3.83 3.83 0 0 1 .11-.468l.405-1.275h.459l-.728 2.142h-.495l-.727-2.142h.459l.402 1.275a4.956 4.956 0 0 1 .115.471v-.003zm3.314-6.827c.258.255.403.6.403.96v9.407c0 .75-.616 1.359-1.375 1.359h-8.25C6.615 19 6 18.391 6 17.64V5.86c0-.751.616-1.36 1.375-1.36h5.847c.365 0 .715.143.972.398l2.403 2.376zm-.648.64L13.546 5.54a.456.456 0 0 0-.213-.119v2.705h2.736a.449.449 0 0 0-.12-.21zm-.324 10.18a.456.456 0 0 0 .458-.453V9.03h-2.979a.684.684 0 0 1-.687-.68V5.407H7.375a.456.456 0 0 0-.458.453v11.782c0 .25.205.453.458.453h8.25z"
  }));
};
var EmptyFileIcon = function EmptyFileIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    fill: colors.GreyBlue,
    fillRule: "evenodd",
    d: "M17.596 7.775l-2.403-2.376A1.383 1.383 0 0 0 14.222 5H8.375A1.372 1.372 0 0 0 7 6.362V18.14c0 .75.616 1.359 1.375 1.359h8.25c.76 0 1.375-.609 1.375-1.36V8.739c0-.36-.146-.708-.404-.963zm-.647.643c.06.06.1.13.12.21h-2.736V5.922c.08.02.152.06.212.119l2.404 2.376zm-.324 10.176h-8.25a.457.457 0 0 1-.458-.453V6.36c0-.248.206-.452.458-.452h5.042v2.945c0 .376.306.68.687.68h2.98v8.607c0 .25-.207.453-.459.453z"
  }));
};
var getFont = functions.config.fonts.getFont;
var colors$1 = functions.config.colors.list;
var LinkWrapper = styled.a.withConfig({
  displayName: "DownloadDocument__LinkWrapper",
  componentId: "fbcnpe-0"
})(["display:flex;align-items:center;border-radius:", ";padding:", ";color:", ";text-decoration:none;background-color:", ";margin:", ";&:hover,&:focus{background-color:", ";color:", ";text-decoration:none;}&:focus{outline:none;}&.focus-visible:focus{box-shadow:0 0 0 .4rem ", ";}&:active{color:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return theme.type === 'secure' ? '.8rem' : '1.2rem';
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.type === 'secure' ? '1rem' : '1.2rem';
}, colors$1.BlueDawn, colors$1.BlueSmoke, function (props) {
  return props.margin;
}, colors$1.BlueLight, colors$1.BlueNight, colors$1.BlueCloudyTransparent, colors$1.BlueDark);
var FileInfo = styled.div.withConfig({
  displayName: "DownloadDocument__FileInfo",
  componentId: "fbcnpe-1"
})(["display:flex;flex-grow:1;align-items:center;svg{flex-shrink:0;width:", ";height:", ";}"], function (_ref3) {
  var theme = _ref3.theme;
  return theme.type === 'secure' ? '2.8rem' : '4rem';
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.type === 'secure' ? '2.8rem' : '4rem';
});
var FileName = styled.div.withConfig({
  displayName: "DownloadDocument__FileName",
  componentId: "fbcnpe-2"
})(["display:flex;flex-wrap:wrap;font-family:", ";font-size:", ";margin-left:", ";color:", ";word-break:break-word;"], function (_ref5) {
  var theme = _ref5.theme;
  return getFont(theme, 'semiBold');
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.type === 'secure' ? '1.4rem' : '1.6rem';
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.type === 'secure' ? '.8rem' : '1.2rem';
}, colors$1.BlueDark);
var TypeContent = styled.div.withConfig({
  displayName: "DownloadDocument__TypeContent",
  componentId: "fbcnpe-3"
})(["margin:0.3rem 1.2rem 0;font-size:1.2rem;color:", ";"], colors$1.GreyBlue);
var DownloadWrapper = styled.div.withConfig({
  displayName: "DownloadDocument__DownloadWrapper",
  componentId: "fbcnpe-4"
})(["display:flex;align-items:center;font-size:1.4rem;font-family:", ";svg{margin-right:.4rem;width:", ";height:", ";fill:currentColor;}"], function (_ref8) {
  var theme = _ref8.theme;
  return getFont(theme, 'semiBold');
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.type === 'secure' ? '2.4rem' : '2.8rem';
}, function (_ref10) {
  var theme = _ref10.theme;
  return theme.type === 'secure' ? '2.4rem' : '2.8rem';
});
var DownloadDocument = function DownloadDocument(_ref11) {
  var href = _ref11.href,
    onClick = _ref11.onClick,
    children = _ref11.children,
    margin = _ref11.margin;
  var getIconByContentType = function getIconByContentType(contentType) {
    switch (contentType) {
      case 'pdf':
        return PdfIcon;
      case 'xls':
      case 'xlsx':
        return ExcelIcon;
      case 'csv':
        return CsvIcon;
      default:
        return EmptyFileIcon;
    }
  };
  var match = href ? href.match(/.(\w+)\/?$/) : null;
  var extension = match ? match[1] : '';
  var contentType = extension.toUpperCase();
  var DocumentIcon = getIconByContentType(extension);
  return /*#__PURE__*/React.createElement(LinkWrapper, {
    margin: margin,
    download: !!href,
    href: href,
    target: href ? '_blank' : null,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(FileInfo, null, /*#__PURE__*/React.createElement(DocumentIcon, {
    color: colors$1.GreyBlue
  }), /*#__PURE__*/React.createElement(FileName, null, children), /*#__PURE__*/React.createElement(TypeContent, null, contentType)), /*#__PURE__*/React.createElement(DownloadWrapper, null, /*#__PURE__*/React.createElement(DownloadIcon, null)));
};
exports.DownloadDocument = DownloadDocument;